import React, { useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader';
import { MODELS, SIDE_ANGLES, getModelAvailableSides } from '../constants/models';

const quality = 3;

const CAPTURE_SIDES = [
  { key: 'front', label: 'Front', angle: SIDE_ANGLES.front, width: 600 * quality, height: 300 * quality },
  { key: 'back', label: 'Back', angle: SIDE_ANGLES.back, width: 600 * quality, height: 300 * quality },
  { key: 'left', label: 'Left', angle: SIDE_ANGLES.left, width: 300 * quality, height: 300 * quality },
  { key: 'right', label: 'Right', angle: SIDE_ANGLES.right, width: 300 * quality, height: 300 * quality },
  { key: 'top', label: 'Top', angle: SIDE_ANGLES.top, width: 600 * quality, height: 300 * quality },
];

const CAPTURE_SIDES_BY_KEY = Object.fromEntries(
  CAPTURE_SIDES.map((side) => [side.key, side])
);

const FILE_EXTENSION = 'png';
const MODEL_PADDING = 1.05;

const slugify = (value) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const waitForFrame = () =>
  new Promise((resolve) => {
    requestAnimationFrame(() => resolve());
  });

const loadModel = (loader, modelPath) =>
  new Promise((resolve, reject) => {
    loader.load(modelPath, resolve, undefined, reject);
  });

const createModelLoader = () => {
  const dracoLoader = new DRACOLoader();
  dracoLoader.setDecoderPath('/draco/gltf/');
  dracoLoader.setDecoderConfig({ type: 'wasm' });

  const gltfLoader = new GLTFLoader();
  gltfLoader.setDRACOLoader(dracoLoader);

  return { gltfLoader, dracoLoader };
};

const dataUrlToBlob = async (dataUrl) => {
  const response = await fetch(dataUrl);
  return response.blob();
};

const downloadBlob = async (dataUrl, fileName) => {
  const blob = await dataUrlToBlob(dataUrl);
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(blobUrl);
};

const disposeMaterial = (material) => {
  Object.values(material).forEach((value) => {
    if (value && typeof value.dispose === 'function') {
      value.dispose();
    }
  });
  material.dispose();
};

const disposeScene = (scene) => {
  scene.traverse((object) => {
    if (!object.isMesh) {
      return;
    }

    object.geometry?.dispose();

    if (Array.isArray(object.material)) {
      object.material.forEach(disposeMaterial);
    } else if (object.material) {
      disposeMaterial(object.material);
    }
  });
};

const createScene = (modelScene) => {
  const scene = new THREE.Scene();
  scene.add(new THREE.AmbientLight(0xffffff, 2.5));

  const keyLight = new THREE.DirectionalLight(0xffffff, 2.5);
  keyLight.position.set(3, 4, 5);
  scene.add(keyLight);

  const fillLight = new THREE.DirectionalLight(0xffffff, 1.5);
  fillLight.position.set(-4, 2, -3);
  scene.add(fillLight);

  scene.add(modelScene);
  return scene;
};

const centerModel = (modelScene) => {
  const box = new THREE.Box3().setFromObject(modelScene);
  const center = box.getCenter(new THREE.Vector3());
  modelScene.position.sub(center);

  const centeredBox = new THREE.Box3().setFromObject(modelScene);
  return centeredBox.getSize(new THREE.Vector3());
};

const getScreenshotFrameForSide = (model, sideKey) => {
  const screenshotFrame = model.screenshotFrame ?? {};
  const sideOverride = screenshotFrame.perSide?.[sideKey] ?? {};

  return {
    verticalScale: sideOverride.verticalScale ?? screenshotFrame.verticalScale ?? 1,
    yOffset: sideOverride.yOffset ?? screenshotFrame.yOffset ?? 0,
  };
};

const getModelBoundingBoxCorners = (box) => {
  const min = box.min;
  const max = box.max;

  return [
    new THREE.Vector3(min.x, min.y, min.z),
    new THREE.Vector3(min.x, min.y, max.z),
    new THREE.Vector3(min.x, max.y, min.z),
    new THREE.Vector3(min.x, max.y, max.z),
    new THREE.Vector3(max.x, min.y, min.z),
    new THREE.Vector3(max.x, min.y, max.z),
    new THREE.Vector3(max.x, max.y, min.z),
    new THREE.Vector3(max.x, max.y, max.z),
  ];
};

const getCameraDirection = (side, model) => {
  const sideOverride = model.screenshotCameraPositions?.[side.key];
  if (sideOverride) {
    return sideOverride.clone();
  }

  return new THREE.Vector3(
    Math.sin(side.angle),
    0,
    Math.cos(side.angle)
  );
};

const frameCameraToModel = (camera, modelScene, modelSize, screenshotFrame = {}, aspect) => {
  const verticalScale = screenshotFrame.verticalScale ?? 1;
  const yOffset = screenshotFrame.yOffset ?? 0;
  const box = new THREE.Box3().setFromObject(modelScene);
  const corners = getModelBoundingBoxCorners(box);
  const projectedPoints = corners.map((corner) => corner.clone().applyMatrix4(camera.matrixWorldInverse));

  const minX = Math.min(...projectedPoints.map((point) => point.x));
  const maxX = Math.max(...projectedPoints.map((point) => point.x));
  const minY = Math.min(...projectedPoints.map((point) => point.y));
  const maxY = Math.max(...projectedPoints.map((point) => point.y));

  const centerX = (minX + maxX) / 2;
  const centerY = (minY + maxY) / 2 + modelSize.y * yOffset;
  const horizontalHalf = (maxX - minX) / 2;
  const verticalHalf = ((maxY - minY) / 2) * verticalScale;
  const frustumHeight = Math.max(verticalHalf * 2, (horizontalHalf * 2) / aspect) * MODEL_PADDING;
  const frustumWidth = frustumHeight * aspect;

  camera.left = centerX - frustumWidth / 2;
  camera.right = centerX + frustumWidth / 2;
  camera.top = centerY + frustumHeight / 2;
  camera.bottom = centerY - frustumHeight / 2;
  camera.updateProjectionMatrix();
};

const createCamera = (side, modelScene, modelSize, model, screenshotFrame = {}) => {
  const aspect = side.width / side.height;
  const maxDimension = Math.max(modelSize.x, modelSize.y, modelSize.z, 1);
  const distance = maxDimension * 2.5;
  const direction = getCameraDirection(side, model).normalize();
  const camera = new THREE.OrthographicCamera(
    -1,
    1,
    1,
    -1,
    0.01,
    maxDimension * 10
  );

  camera.up.copy(model.cameraUpBySide?.[side.key] ?? THREE.Object3D.DEFAULT_UP);
  camera.position.copy(direction.multiplyScalar(distance));
  camera.lookAt(0, 0, 0);
  camera.updateMatrixWorld(true);
  frameCameraToModel(camera, modelScene, modelSize, screenshotFrame, aspect);
  camera.updateProjectionMatrix();

  return camera;
};

const createRenderer = () => {
  const renderer = new THREE.WebGLRenderer({
    antialias: true,
    alpha: true,
    preserveDrawingBuffer: true,
  });

  renderer.setClearColor(0xffffff, 0);
  renderer.setPixelRatio(1);

  if (THREE.SRGBColorSpace) {
    renderer.outputColorSpace = THREE.SRGBColorSpace;
  }

  return renderer;
};

function ModelScreenshotCapture() {
  const [isCapturing, setIsCapturing] = useState(false);
  const [status, setStatus] = useState('Ready to capture.');
  const [capturedFiles, setCapturedFiles] = useState([]);
  const abortRef = useRef(false);
  const canWriteToFolder = useMemo(() => Boolean(window.showDirectoryPicker), []);
  const captureFilters = useMemo(() => {
    const params = new URL(window.location.href).searchParams;
    return {
      modelName: params.get('captureModel'),
      side: params.get('captureSide'),
    };
  }, []);

  const captureModelSide = async (renderer, gltf, side, model) => {
    renderer.setSize(side.width, side.height, false);

    const modelScene = gltf.scene.clone(true);
    const modelSize = centerModel(modelScene);
    const scene = createScene(modelScene);
    const camera = createCamera(
      side,
      modelScene,
      modelSize,
      model,
      getScreenshotFrameForSide(model, side.key)
    );

    renderer.render(scene, camera);
    await waitForFrame();
    renderer.render(scene, camera);

    const dataUrl = renderer.domElement.toDataURL(`image/${FILE_EXTENSION}`);

    return dataUrl;
  };

  const writeImageFile = async (directoryHandle, modelSlug, fileName, dataUrl) => {
    const modelDirectoryHandle = await directoryHandle.getDirectoryHandle(modelSlug, {
      create: true,
    });
    const fileHandle = await modelDirectoryHandle.getFileHandle(fileName, {
      create: true,
    });
    const writable = await fileHandle.createWritable();
    await writable.write(await dataUrlToBlob(dataUrl));
    await writable.close();
  };

  const captureModels = async ({ writeToFolder }) => {
    setIsCapturing(true);
    setCapturedFiles([]);
    abortRef.current = false;

    const { gltfLoader, dracoLoader } = createModelLoader();
    const renderer = createRenderer();
    const captured = [];
    let outputDirectoryHandle = null;

    try {
      if (writeToFolder) {
        outputDirectoryHandle = await window.showDirectoryPicker({
          mode: 'readwrite',
        });
      }

      const modelsToCapture = captureFilters.modelName
        ? MODELS.filter((model) => model.name === captureFilters.modelName)
        : MODELS;

      for (const model of modelsToCapture) {
        if (abortRef.current) {
          setStatus('Capture stopped.');
          break;
        }

        const modelSlug = slugify(model.name);
        const sidesToCapture = getModelAvailableSides(model)
          .filter((sideKey) => !captureFilters.side || sideKey === captureFilters.side)
          .map((sideKey) => ({
          ...CAPTURE_SIDES_BY_KEY[sideKey],
          angle: model.sideAngles?.[sideKey] ?? CAPTURE_SIDES_BY_KEY[sideKey].angle,
          }));
        setStatus(`Loading ${model.name}...`);
        const gltf = await loadModel(gltfLoader, "/models/" + model.name + '/' + model.gltf);

        for (const side of sidesToCapture) {
          if (abortRef.current) {
            break;
          }

          const fileName = outputDirectoryHandle
            ? `${side.key}.${FILE_EXTENSION}`
            : `${modelSlug}-${side.key}.${FILE_EXTENSION}`;
          setStatus(`Capturing ${model.name} ${side.label.toLowerCase()}...`);
          const dataUrl = await captureModelSide(renderer, gltf, side, model);

          if (outputDirectoryHandle) {
            await writeImageFile(outputDirectoryHandle, model.name, fileName, dataUrl);
          } else {
            await downloadBlob(dataUrl, fileName);
            await new Promise((resolve) => setTimeout(resolve, 250));
          };
          captured.push(outputDirectoryHandle ? `${model.name}/${fileName}` : fileName);
          setCapturedFiles([...captured]);
        }

        disposeScene(gltf.scene);
      }

      if (!abortRef.current) {
        setStatus(`Captured ${captured.length} images.`);
      }
    } catch (error) {
      setStatus(`Capture failed: ${error.message}`);
    } finally {
      renderer.dispose();
      dracoLoader.dispose();
      setIsCapturing(false);
    }
  };

  const stopCapture = () => {
    abortRef.current = true;
  };

  return (
    <div style={{ border: '1px solid #ccc', padding: 12, marginBottom: 16, width: 600 }}>
      <h4 style={{ marginTop: 0 }}>Internal Model Screenshot Capture</h4>
      <p style={{ marginTop: 0 }}>
        Captures front/back/top at 600x300 and left/right at 300x300 using
        orthographic framing. Use these images as the 2D model backgrounds.
      </p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {canWriteToFolder ? (
          <button
            type="button"
            disabled={isCapturing}
            onClick={() => captureModels({ writeToFolder: true })}
            style={{ borderRadius: 0, borderWidth: 1, padding: '8px 10px', background: '#eee' }}
          >
            Capture to Folder
          </button>
        ) : null}
        <button
          type="button"
          disabled={isCapturing}
          onClick={() => captureModels({ writeToFolder: false })}
          style={{ borderRadius: 0, borderWidth: 1, padding: '8px 10px', background: '#eee' }}
        >
          Download PNGs
        </button>
        {isCapturing ? (
          <button
            type="button"
            onClick={stopCapture}
            style={{ borderRadius: 0, borderWidth: 1, padding: '8px 10px', background: '#eee' }}
          >
            Stop
          </button>
        ) : null}
      </div>
      <p>{status}</p>
      {capturedFiles.length ? (
        <div style={{ maxHeight: 160, overflowY: 'auto', fontFamily: 'var(--font-mono)', fontSize: 12 }}>
          {capturedFiles.map((fileName) => (
            <div key={fileName}>{fileName}</div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default ModelScreenshotCapture;
